import { useEffect, useState, Suspense } from "react";
import { useParams, useNavigate, Outlet } from "react-router-dom";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { OrgSidebar } from "./OrgSidebar";

import { cn } from "@/lib/utils";

// Convert hex to HSL values for CSS variables
function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "252 70% 60%";
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function OrganizationLayout() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { setOrganizationBySlug, organization, loading } = useOrganization();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [notFound, setNotFound] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("sidebar-collapsed") === "true";
    }
    return false;
  });

  useEffect(() => {
    const initOrg = async () => {
      if (!orgSlug || !user || authLoading) return;
      const success = await setOrganizationBySlug(orgSlug);
      if (!success) setNotFound(true);
    };
    initOrg();
  }, [orgSlug, user, authLoading]);

  useEffect(() => {
    if (organization?.primary_color) {
      const primaryHsl = hexToHsl(organization.primary_color);
      document.documentElement.style.setProperty('--primary', primaryHsl);
      document.documentElement.style.setProperty('--sidebar-primary', primaryHsl);
      document.documentElement.style.setProperty('--ring', primaryHsl);
      const [h] = primaryHsl.split(' ');
      document.documentElement.style.setProperty('--secondary', `${h} 30% 95%`);
      document.documentElement.style.setProperty('--secondary-foreground', `${h} 70% 45%`);
      document.documentElement.style.setProperty('--accent', `${h} 50% 95%`);
      document.documentElement.style.setProperty('--accent-foreground', `${h} 70% 50%`);
      document.documentElement.style.setProperty('--sidebar-accent', `${h} 50% 97%`);
      document.documentElement.style.setProperty('--sidebar-accent-foreground', `${h} 70% 50%`);
    }
    return () => {
      ['--primary','--sidebar-primary','--ring','--secondary','--secondary-foreground','--accent','--accent-foreground','--sidebar-accent','--sidebar-accent-foreground'].forEach(v => document.documentElement.style.removeProperty(v));
    };
  }, [organization?.primary_color]);

  useEffect(() => {
    const handler = () => {
      setSidebarCollapsed(localStorage.getItem("sidebar-collapsed") === "true");
    };
    window.addEventListener("storage", handler);
    window.addEventListener("sidebar-toggle", handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("sidebar-toggle", handler);
    };
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [authLoading, user, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Organization Not Found</h1>
          <p className="text-muted-foreground mb-4">You don't have access to this organization or it doesn't exist.</p>
          <button onClick={() => navigate("/")} className="text-primary hover:underline">Go back home</button>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-background overflow-x-hidden">
      <OrgSidebar />
      <main className={cn("flex-1 min-h-screen overflow-x-hidden min-w-0 transition-[margin] duration-200", sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-64")}>
        <Suspense fallback={
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
